import posthog from "posthog-js/dist/module.no-external";
import type {
  CaptureOptions,
  CaptureResult,
  PostHogConfig,
} from "posthog-js/dist/module.no-external";

export const TOOLS = [
  "studio_create",
  "studio_template",
  "studio_import",
  "studio_export_json",
  "rectangle",
  "diamond",
  "ellipse",
  "arrow",
  "line",
  "freedraw",
  "text",
  "image",
  "frame",
  "embeddable",
  "drawShape",
  "create",
  "move",
  "resize",
  "rotate",
  "delete",
  "undo",
  "redo",
  "paste",
  "edit",
  "export_json",
  "export_png",
  "export_svg",
  "export_clipboard",
  "autosave",
] as const;
export type Tool = (typeof TOOLS)[number];
type Properties = Record<string, string | number | boolean>;
interface Config {
  enabled: boolean;
  token: string;
  projectId: string;
  apiHost: string;
  uiHost: string;
  environment: string;
  release: string;
}
interface Transport {
  init: (token: string, config: Partial<PostHogConfig>) => unknown;
  capture: (
    event: string,
    properties: Properties,
    options: CaptureOptions,
  ) => unknown;
}
const EVENTS = new Set([
  "tool_started",
  "tool_succeeded",
  "tool_failed",
  "tool_cancelled",
  "tool_used",
  "persistence_health",
]);
const ALLOWED = new Set([
  "app",
  "environment",
  "release",
  "schema_version",
  "tool",
  "event_id",
  "attempt_id",
  "attempt_started_at",
  "duration_ms",
  "error_code",
  "failure_kind",
  "success_count",
  "failure_count",
  "outcome",
  "$host",
  "$raw_user_agent",
  "$browser",
  "$browser_version",
  "$os",
  "$os_version",
  "$device_type",
  "$lib",
  "$lib_version",
  "distinct_id",
  "$process_person_profile",
  "$geoip_disable",
  "$cookieless_mode",
  "token",
]);

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_LABEL = /^[a-zA-Z0-9 ._()-]{1,80}$/;
/** Validate every allowed property without accepting content-shaped strings. */
const allowedValue = (key: string, value: unknown): boolean => {
  if (!ALLOWED.has(key)) {
    return false;
  }
  if (["event_id", "attempt_id"].includes(key)) {
    return typeof value === "string" && UUID.test(value);
  }
  if (key === "tool") {
    return TOOLS.includes(value as Tool);
  }
  if (key === "app") {
    return value === "drawola";
  }
  if (key === "environment") {
    return value === "production" || value === "validation";
  }
  if (key === "schema_version") {
    return value === 1;
  }
  if (key === "error_code") {
    return [
      "none",
      "storage_quota",
      "operation_failed",
      "cancelled",
      "invalid_input",
      "document_missing",
    ].includes(value as string);
  }
  if (key === "failure_kind") {
    return value === "technical" || value === "rejected";
  }
  if (key === "outcome") {
    return ["completed", "download_handoff", "clipboard_written"].includes(
      value as string,
    );
  }
  if (["duration_ms", "success_count", "failure_count"].includes(key)) {
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value >= 0 &&
      Number.isSafeInteger(value)
    );
  }
  if (key === "attempt_started_at") {
    return (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value) &&
      Number.isFinite(Date.parse(value))
    );
  }
  if (key === "$raw_user_agent") {
    const userAgent = navigator.userAgent;
    return (
      value ===
      (userAgent.length > 1000 ? `${userAgent.slice(0, 997)}...` : userAgent)
    );
  }
  if (key === "$host") {
    return value === window.location.host;
  }
  if (key === "distinct_id") {
    return value === "$posthog_cookieless";
  }
  if (key === "$cookieless_mode" || key === "$geoip_disable") {
    return value === true;
  }
  if (key === "$process_person_profile") {
    return value === false;
  }
  if (key === "token") {
    return typeof value === "string" && /^phc_[A-Za-z0-9_]{1,200}$/.test(value);
  }
  if (key === "release") {
    return typeof value === "string" && /^[A-Za-z0-9._-]{1,64}$/.test(value);
  }
  if (key === "$browser_version") {
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value >= 0 &&
      value < 10000
    );
  }
  return typeof value === "string" && SAFE_LABEL.test(value);
};

/** Remove unapproved properties and top-level person updates before transmission. */
export const sanitizeEvent = (
  event: CaptureResult | null,
): CaptureResult | null => {
  if (!event || !EVENTS.has(event.event) || !UUID.test(event.uuid)) {
    return null;
  }
  const properties = Object.fromEntries(
    Object.entries(event.properties).filter(([key, value]) =>
      allowedValue(key, value),
    ),
  );
  if (
    properties.app !== "drawola" ||
    !properties.tool ||
    properties.event_id !== event.uuid
  ) {
    return null;
  }
  return {
    event: event.event,
    uuid: event.uuid,
    timestamp:
      event.timestamp instanceof Date &&
      Number.isFinite(event.timestamp.getTime())
        ? event.timestamp
        : undefined,
    properties,
  };
};

/** Classify failures without forwarding error messages or document content. */
export const errorCode = (error: unknown): string => {
  const name =
    error && typeof error === "object" && "name" in error ? error.name : "";
  return name === "QuotaExceededError"
    ? "storage_quota"
    : name === "AbortError"
    ? "cancelled"
    : "operation_failed";
};

/** Collect explicit anonymous product events without affecting product operations. */
export class Analytics {
  private ready = false;
  private config?: Config;
  /** Accept a transport so behavior can be tested without sending live events. */
  constructor(private readonly sdk: Transport) {}
  /** Initialize once only when the complete EU project configuration is enabled. */
  initialize(config: Config): void {
    if (
      this.ready ||
      !config.enabled ||
      !config.token.startsWith("phc_") ||
      config.projectId !== "286655" ||
      config.apiHost !== "https://eu.i.posthog.com" ||
      config.uiHost !== "https://eu.posthog.com" ||
      !["production", "validation"].includes(config.environment)
    ) {
      return;
    }
    try {
      this.sdk.init(config.token, {
        api_host: config.apiHost,
        ui_host: config.uiHost,
        cookieless_mode: "always",
        person_profiles: "never",
        persistence: "memory",
        disable_persistence: true,
        autocapture: false,
        enable_heatmaps: false,
        rageclick: false,
        save_campaign_params: false,
        save_referrer: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_exceptions: false,
        capture_performance: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_external_dependency_loading: true,
        advanced_disable_flags: true,
        advanced_disable_feature_flags: true,
        enable_recording_console_log: false,
        before_send: sanitizeEvent,
      });
      this.config = config;
      this.ready = true;
    } catch {
      /* Analytics is optional when a browser blocks the SDK. */
    }
  }
  /** Emit one permitted event with a UUID reused by the SDK retry queue. */
  private emit(event: string, tool: Tool, properties: Properties = {}): void {
    if (!this.ready || !TOOLS.includes(tool)) {
      return;
    }
    try {
      const id = crypto.randomUUID();
      this.sdk.capture(
        event,
        {
          ...properties,
          app: "drawola",
          environment: this.config!.environment,
          release: this.config!.release,
          schema_version: 1,
          tool,
          event_id: id,
        },
        { uuid: id },
      );
    } catch {
      /* Telemetry failure must not alter the product result. */
    }
  }
  /** Start a single operation whose terminal result is accepted once. */
  start(tool: Tool): Attempt {
    let attemptId: string;
    try {
      attemptId = crypto.randomUUID();
    } catch {
      // Old or restricted browsers still need to complete the product operation.
      const noop = () => undefined;
      return { succeed: noop, fail: noop, reject: noop, cancel: noop };
    }
    const started = Date.now();
    let ended = false;
    const common = {
      attempt_id: attemptId,
      attempt_started_at: new Date(started).toISOString(),
    };
    this.emit("tool_started", tool, common);
    /** Finish the accepted attempt once, including duplicate callback protection. */
    const finish = (event: string, extra: Properties = {}): void => {
      if (ended) {
        return;
      }
      ended = true;
      this.emit(event, tool, {
        ...common,
        duration_ms: Math.max(0, Date.now() - started),
        ...extra,
      });
    };
    return {
      succeed: (outcome = "completed") => finish("tool_succeeded", { outcome }),
      fail: (error) =>
        errorCode(error) === "cancelled"
          ? finish("tool_cancelled")
          : finish("tool_failed", {
              error_code: errorCode(error),
              failure_kind: "technical",
            }),
      reject: (code) =>
        finish("tool_failed", { error_code: code, failure_kind: "rejected" }),
      cancel: () => finish("tool_cancelled"),
    };
  }
  /** Record a committed edit, never a tool selection or scene render. */
  used(tool: Tool): void {
    this.emit("tool_used", tool);
  }
  /** Record a background persistence summary outside the usage denominator. */
  health(success: number, failure: number, code = "none"): void {
    this.emit("persistence_health", "autosave", {
      success_count: success,
      failure_count: failure,
      error_code: code,
    });
  }
}
export interface Attempt {
  succeed: (
    outcome?: "completed" | "download_handoff" | "clipboard_written",
  ) => void;
  fail: (error: unknown) => void;
  reject: (
    code: "storage_quota" | "invalid_input" | "document_missing",
  ) => void;
  cancel: () => void;
}
export const analytics = new Analytics(posthog);
/** Read only build-time configuration; URLs cannot enable analytics. */
export const initializeAnalytics = (): void =>
  analytics.initialize({
    enabled: import.meta.env.VITE_APP_POSTHOG_ENABLED === "true",
    token: import.meta.env.VITE_APP_POSTHOG_TOKEN ?? "",
    projectId: import.meta.env.VITE_APP_POSTHOG_PROJECT_ID ?? "",
    apiHost: import.meta.env.VITE_APP_POSTHOG_API_HOST ?? "",
    uiHost: import.meta.env.VITE_APP_POSTHOG_UI_HOST ?? "",
    environment: import.meta.env.VITE_APP_POSTHOG_ENVIRONMENT ?? "validation",
    release: import.meta.env.VITE_APP_GIT_SHA || "development",
  });
